import { type VariantProps, cva } from 'class-variance-authority';
import { CheckIcon, ChevronDown, PlusIcon, XIcon } from 'lucide-react';
import * as React from 'react';

import { TooltipComponent } from '@components/core/tooltip.component';
import { cn } from '@lib/utils';
import { BaseSelectOption, mapToSelectOptions } from '@utils/select.util';
import { Badge } from './badge';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Separator } from './separator';

const multiSelectVariants = cva('transition ease-in-out delay-150 duration-300', {
  variants: {
    variant: {
      default: 'border-transparent bg-gray-200 text-gray-700 hover:bg-gray-200',
      destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
      inverted: 'inverted',
      secondary: 'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type MultiSelectProps = React.ComponentProps<'button'> &
  VariantProps<typeof multiSelectVariants> & {
    asChild?: boolean;
    hasError?: boolean;
    isCreating?: boolean;
    modalPopover?: boolean;
    noResultsMessage?: React.ReactNode;
    onCreateOption?: (value: string) => Promise<void> | void;
    onValueChange: (value: string[]) => void;
    options: BaseSelectOption[];
    placeholder?: string;
    renderBadge?: (params: {
      description?: string;
      label: string;
      remove: () => void;
      value: string;
    }) => React.ReactNode;
    renderOption?: (params: { description?: string; label: string; value: string }) => React.ReactNode;
    trailingAction?: React.ReactNode;
    value: string[];
  };

export const MultiSelect = (props: MultiSelectProps) => {
  const {
    asChild = false,
    className,
    hasError = false,
    isCreating = false,
    modalPopover = false,
    noResultsMessage = 'No results found.',
    onCreateOption,
    onValueChange,
    options,
    placeholder = 'Select options',
    ref,
    renderBadge,
    renderOption,
    trailingAction,
    value = [],
    variant,
    ...buttonProps
  } = props;
  const ariaInvalid = hasError || buttonProps['aria-invalid'];

  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [trailingActionWidth, setTrailingActionWidth] = React.useState(32);
  const trailingActionRef = React.useRef<HTMLDivElement>(null);

  const selectedValues = Array.isArray(value) ? value : [];
  const trailingActionSpacerWidth = trailingAction ? trailingActionWidth + 4 : 0;

  const normalizedOptions = React.useMemo(() => mapToSelectOptions(options), [options]);
  const optionByValue = React.useMemo(() => {
    return new Map(normalizedOptions.map((option) => [option.value, option]));
  }, [normalizedOptions]);

  const groupedOptions = React.useMemo(() => {
    const visible = normalizedOptions.filter((option) => !option.hidden || selectedValues.includes(option.value));
    const hasGroups = visible.some((option) => option.group);
    if (!hasGroups) return [{ group: '', options: visible }];

    const groups = new Map<string, typeof visible>();
    for (const option of visible) {
      const group = option.group ?? '';
      const existing = groups.get(group);
      if (existing) {
        existing.push(option);
      } else {
        groups.set(group, [option]);
      }
    }
    return Array.from(groups, ([group, options]) => ({ group, options })).sort((a, b) =>
      a.group.localeCompare(b.group),
    );
  }, [normalizedOptions, selectedValues]);

  const trimmedSearch = searchValue.trim().toLowerCase();
  const canCreate =
    onCreateOption &&
    trimmedSearch.length > 0 &&
    !normalizedOptions.some((option) => option.label.toLowerCase() === trimmedSearch);

  const handleCreateOption = async () => {
    if (!canCreate || isCreating) return;
    await onCreateOption(searchValue.trim());
    setSearchValue('');
  };

  const toggleOption = (optionValue: string) => {
    if (optionByValue.get(optionValue)?.disabled) {
      return;
    }

    const nextSelectedValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((valueItem) => valueItem !== optionValue)
      : [...selectedValues, optionValue];
    onValueChange(nextSelectedValues);
  };

  const handleClear = () => {
    const nextSelectedValues = selectedValues.filter((selectedValue) => {
      return optionByValue.get(selectedValue)?.disabled;
    });
    onValueChange(nextSelectedValues);
  };

  const handleTogglePopover = () => {
    setIsPopoverOpen((prev) => !prev);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (canCreate) {
        event.preventDefault();
        void handleCreateOption();
      } else if (!event.currentTarget.value) {
        event.preventDefault();
        setIsPopoverOpen(true);
      }
    } else if (event.key === 'Backspace' && !event.currentTarget.value && selectedValues.length > 0) {
      const newSelectedValues = [...selectedValues];
      const lastSelectedValue = newSelectedValues[newSelectedValues.length - 1];
      if (lastSelectedValue && optionByValue.get(lastSelectedValue)?.disabled) {
        return;
      }

      newSelectedValues.pop();
      onValueChange(newSelectedValues);
    }
  };

  React.useEffect(() => {
    const trailingActionElement = trailingActionRef.current;
    if (!trailingActionElement || !trailingAction) {
      return;
    }

    const updateTrailingActionWidth = () => {
      setTrailingActionWidth(Math.ceil(trailingActionElement.getBoundingClientRect().width));
    };

    updateTrailingActionWidth();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(updateTrailingActionWidth);
    resizeObserver.observe(trailingActionElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [trailingAction]);

  return (
    <Popover
      modal={modalPopover}
      onOpenChange={setIsPopoverOpen}
      open={isPopoverOpen}
    >
      <div className="relative w-full">
        <PopoverTrigger asChild>
          <Button
            {...buttonProps}
            aria-invalid={ariaInvalid}
            className={cn(
              'flex h-fit min-h-10 w-full items-center justify-between rounded-md border border-gray-200 bg-gray-100',
              'font-normal text-base hover:bg-gray-100 md:text-sm',
              'focus-visible:border-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
              'aria-invalid:border-red-500 aria-invalid:focus-visible:border-red-500 aria-invalid:focus-visible:ring-red-200',
              'disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
              '[&_svg]:pointer-events-auto',
              selectedValues.length > 0 ? 'p-1' : 'px-3 py-2 h-10',
              className,
            )}
            onClick={handleTogglePopover}
            ref={ref}
          >
            {selectedValues.length > 0 ? (
              <div className="flex items-stretch justify-between w-full">
                <div className="flex flex-wrap items-center flex-1 gap-1 min-w-0">
                  {selectedValues.map((selectedValue) => {
                    const option = normalizedOptions.find((optionItem) => optionItem.value === selectedValue);
                    const IconComponent = option?.icon;
                    const group = option?.group?.trim();
                    const label = option?.label ?? selectedValue;
                    const displayLabel = group ? `${group}: ${label}` : label;
                    const isOptionDisabled = Boolean(option?.disabled);
                    const badge = (
                      <Badge
                        key={selectedValue}
                        className={cn(
                          multiSelectVariants({ variant }),
                          isOptionDisabled && 'cursor-not-allowed opacity-60',
                        )}
                      >
                        {IconComponent && <IconComponent className="h-4 w-4 mr-2" />}
                        {displayLabel}
                        {!isOptionDisabled && (
                          <XIcon
                            className="ml-2 h-4 w-4 cursor-pointer hover:text-red-500"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleOption(selectedValue);
                            }}
                          />
                        )}
                      </Badge>
                    );

                    if (renderBadge) {
                      return (
                        <React.Fragment key={selectedValue}>
                          {renderBadge({
                            description: option?.description,
                            label: displayLabel,
                            remove: () => toggleOption(selectedValue),
                            value: selectedValue,
                          })}
                        </React.Fragment>
                      );
                    }

                    if (isOptionDisabled && option?.disabledTooltip) {
                      return (
                        <TooltipComponent
                          key={selectedValue}
                          side="right"
                          trigger={<span className="inline-flex">{badge}</span>}
                        >
                          {option.disabledTooltip}
                        </TooltipComponent>
                      );
                    }

                    return badge;
                  })}
                </div>
                <div className="ml-2 flex shrink-0 items-center gap-2">
                  <XIcon
                    className="size-4 cursor-pointer text-muted-foreground hover:text-red-500"
                    data-multi-select-clear
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}
                  />
                  <Separator
                    className="h-6"
                    orientation="vertical"
                  />
                  <ChevronDown
                    className="size-3.5! cursor-pointer text-muted-foreground"
                    data-multi-select-chevron
                  />
                  {trailingAction && (
                    <span
                      className="block h-7 shrink-0"
                      data-multi-select-trailing-spacer
                      style={{ width: trailingActionSpacerWidth }}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full mx-auto">
                <span className="text-gray-400">{placeholder}</span>
                <div className="flex shrink-0 items-center gap-1">
                  <ChevronDown
                    className="size-3.5! cursor-pointer text-muted-foreground"
                    data-multi-select-chevron
                  />
                  {trailingAction && (
                    <span
                      className="block h-7 shrink-0"
                      data-multi-select-trailing-spacer
                      style={{ width: trailingActionSpacerWidth }}
                    />
                  )}
                </div>
              </div>
            )}
          </Button>
        </PopoverTrigger>
        {trailingAction && (
          <div
            className="absolute right-1.5 top-1/2 z-10 flex -translate-y-1/2 items-center"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            ref={trailingActionRef}
          >
            {trailingAction}
          </div>
        )}
      </div>
      <PopoverContent
        align="start"
        className="w-auto p-0"
        onEscapeKeyDown={() => setIsPopoverOpen(false)}
      >
        <Command>
          <CommandInput
            onKeyDown={handleInputKeyDown}
            onValueChange={setSearchValue}
            placeholder="Search..."
            value={searchValue}
          />
          <CommandList>
            <CommandEmpty>
              {canCreate ? (
                <button
                  className="flex w-full cursor-pointer items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent rounded-sm disabled:opacity-50"
                  disabled={isCreating}
                  onClick={() => void handleCreateOption()}
                  type="button"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>{isCreating ? 'Creating...' : `Create "${searchValue.trim()}"`}</span>
                </button>
              ) : (
                <span className="px-2 py-1.5 text-sm text-muted-foreground">{noResultsMessage}</span>
              )}
            </CommandEmpty>
            {groupedOptions.map(({ group, options: groupOptions }) => (
              <CommandGroup
                heading={group || undefined}
                key={group || '__ungrouped'}
              >
                {groupOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  const optionContent = (
                    <>
                      {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                      {renderOption ? (
                        renderOption({
                          description: option.description,
                          label: option.label,
                          value: option.value,
                        })
                      ) : (
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate">{option.label}</span>
                          {option.description && (
                            <span className="text-xs text-muted-foreground truncate">{option.description}</span>
                          )}
                        </div>
                      )}
                    </>
                  );

                  return (
                    <CommandItem
                      className={cn('max-w-lg', option.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}
                      disabled={option.disabled}
                      key={option.value}
                      onSelect={() => toggleOption(option.value)}
                    >
                      <div
                        className={cn(
                          'mr-2 flex h-4 w-4 p-2 items-center justify-center rounded-sm border border-primary/60',
                          isSelected ? 'border-[#3D2EFF]! bg-[#3D2EFF] text-white' : '[&_svg]:invisible',
                        )}
                      >
                        <CheckIcon className="" />
                      </div>
                      {option.disabled && option.disabledTooltip ? (
                        <TooltipComponent
                          side="right"
                          trigger={<div className="w-full">{optionContent}</div>}
                        >
                          {option.disabledTooltip}
                        </TooltipComponent>
                      ) : (
                        optionContent
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
            {canCreate && normalizedOptions.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    className="cursor-pointer"
                    disabled={isCreating}
                    onSelect={() => void handleCreateOption()}
                  >
                    <PlusIcon className="mr-2 h-4 w-4" />
                    <span>{isCreating ? 'Creating...' : `Create "${searchValue.trim()}"`}</span>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
            <CommandSeparator />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

MultiSelect.displayName = 'MultiSelect';
